import { createSelector } from '@reduxjs/toolkit';
import { getOfferWithMinPricingSummary, mapActivityDetail, selectDisplayPricing } from 'gql/service/graph/activityService';
import { AvailabilityState } from 'component/page/detail/store/availability';
import { RootState } from 'store/store.types';
import { ActivityWithContent } from 'gql/autogen/ActivityWithContent';
import { isDefaultGqlState } from 'store/api/activitiesGqlApi';
import { Optional } from 'core/type/generic';
import { OfferDetail } from 'gql/autogen/OfferDetail';
import { PageContentStatus } from 'core/type/PageContentStatus';
import { selectBehaviorFromBootstrap, selectConfigFromBootstrap, selectExperimentsFromBootstrap } from 'store/slice/bootstrap';
import { getShoppingCartBehavior, selectIsAddAnyItemsToCart } from 'core/utils/cart';
import { selectDetailState } from 'component/page/detail/store/detail.selectors';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { Offer } from 'gql/autogen/Offer';
import { ActivityAvailabilityStatus } from 'gql/autogen/ActivityAvailabilityStatus';
import { format } from 'date-fns';
import { ContinueYourBookingNotificationType, OfferNotificationType } from 'component/page/detail/store/availability.types';
import { selectOfferLevelCalendarsFromCalendarState } from 'component/page/detail/store/calendar.selectors';
import { getSelectedOfferFromOfferGrouping } from 'component/page/detail/PackageOptions/PackageOptionsUtils';
import { AvailabilitySchedule } from 'gql/autogen/AvailabilitySchedule';
import { paxTypeMapper } from 'core/type/addToCartMapper';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

export const selectActivityDetail = createSelector(selectDetailState, (state) => mapActivityDetail(state));
export const selectActivityDetailOffer = createSelector(selectActivityDetail, (state) => state.offerDetails || []);
export const selectActivityFromDetail = createSelector(selectDetailState, (details) => details?.detailsResponse?.result?.activity || ({} as ActivityWithContent));
export const selectActivityContentLocale = createSelector(selectActivityFromDetail, (activity) => activity?.content?.contentLocale || 'en');

export const selectAvailabilityState = (state: RootState): AvailabilityState => state.availability;
export const selectAvailabilityIsLoading = createSelector(selectAvailabilityState, (state) => state.isLoading);
export const selectAvailabilityIsSearchCriteriaUpdated = createSelector(selectAvailabilityState, (state) => state.isSearchCriteriaUpdated);
export const selectAvailabilitySearchCriteriaStatus = createSelector(selectAvailabilityState, (state) => state.searchCriteriaStatus);
// Due to supplier limitation we will only have bookingRequirement inside availability for KKDAY and fall back to state.bookingRequirement from details for Viator
export const selectSelectedBookingRequirement = createSelector(selectAvailabilityState, (state) => state.selectedBookingRequirement);
export const selectTotalPax = createSelector(selectAvailabilityState, (state) => state.totalPax);
export const selectOfferSelected = createSelector(selectAvailabilityState, (state) => state.selectedOffer);
export const selectOfferOptionTokenFromOfferSelected = createSelector(
    selectOfferSelected,
    (state) => (state.offer?.enrichedOfferOption && state.offer?.enrichedOfferOption[0].offerOptionToken) || '',
);
export const selectOfferPricing = createSelector(selectAvailabilityState, (state) => state.availabilityResponse?.result?.activity?.offerPricing);
const selectAvailableOffers = createSelector(
    selectAvailabilityState,
    (state) =>
        state.availabilityResponse?.result?.activity?.offerPricing?.offers?.filter(
            (offer) => offer.isAvailable && offer.enrichedOfferOption && offer.enrichedOfferOption.map((enrichOffer) => enrichOffer.isAvailable),
        ) || [],
);
export const selectOfferDetailRefSelected = createSelector(selectOfferSelected, (state) => state.offer?.offerDetailRef);
export const selectOfferGroupIdFromSelectedOfferGroup = createSelector(selectOfferSelected, (state) => state.offer?.offerGroupId || -1);
export const selectActivityIdFromSelectedOffer = createSelector(selectOfferSelected, (state) => state.offer?.activityId);

export const selectSelectedSpecifications = createSelector(selectAvailabilityState, selectOfferGroupIdFromSelectedOfferGroup, (state, selectedOfferId) => {
    const selectedSpecifications = state.offerOptionsState[selectedOfferId]?.selectedSpecifications || [];
    return selectedSpecifications;
});

export const selectPossibleOfferOptions = createSelector(selectAvailabilityState, selectOfferGroupIdFromSelectedOfferGroup, (state, selectedOfferId) => {
    const possibleOfferOptions = state.offerOptionsState[selectedOfferId]?.possibleOfferOptions || [];
    return possibleOfferOptions;
});

export const selectBasedCalendarOfferIds = createSelector(selectAvailabilityState, selectOfferGroupIdFromSelectedOfferGroup, (state, selectedOfferId) => {
    const basedCalendarOfferIds = state.offerOptionsState[selectedOfferId]?.basedCalendarOfferIds || [];
    return basedCalendarOfferIds;
});

export const selectUnavailableDatesFromAvailabilityResponse = createSelector(selectAvailabilityState, selectOfferGroupIdFromSelectedOfferGroup, (state, selectedOfferId) => {
    const unavailableDatesFromAvailabilityResponse = state.offerOptionsState[selectedOfferId]?.unavailableDatesFromAvailabilityResponse || [];
    return unavailableDatesFromAvailabilityResponse;
});
export const selectFinalOfferOption = createSelector(selectPossibleOfferOptions, (possibleOffers) => {
    return getSelectedOfferFromOfferGrouping(possibleOffers);
});

export const selectFinalOfferId = createSelector(selectFinalOfferOption, (finalOfferOption) => finalOfferOption?.offerId);

export const selectFinalOfferOptionId = createSelector(selectFinalOfferOption, (finalOfferOption) => finalOfferOption?.offerOptionId);

export const selectAllOfferOptions = createSelector(selectAvailabilityState, selectOfferGroupIdFromSelectedOfferGroup, (state, selectedOfferId) => {
    const allOfferOptions = state.offerOptionsState[selectedOfferId]?.allOfferOptions || [];
    return allOfferOptions;
});

export const selectPassengerOption = createSelector(selectAvailabilityState, selectOfferGroupIdFromSelectedOfferGroup, (state, selectedOfferId) => {
    const paxes = state.offerOptionsState[selectedOfferId]?.passengers || [];
    return paxes;
});

// Returns max quantity of the offer or Infinity if not available
export const selectAvailabilityQuantitySelectedOffer = createSelector(selectFinalOfferOption, selectExperimentsFromBootstrap, (finalOfferOption, experiments) => {
    return isB(ExperimentId.ACT_4598, experiments) ? finalOfferOption?.availableQuantity || Infinity : Infinity;
});

export const selectOfferLevelSelectedDate = createSelector(
    selectAvailabilityState,
    selectOfferGroupIdFromSelectedOfferGroup,
    (state, selectedOfferId) => state.offerOptionsState[selectedOfferId]?.selectedDate || state.selectedDate,
);

export const selectOfferLevelPaxRequirements = createSelector(selectOfferPricing, selectPossibleOfferOptions, (state, possibleOptions) => {
    const bookingRequirementRefId = possibleOptions[0]?.bookingRequirementRefId;
    const bookingRequirement = state?.bookingRequirements && state.bookingRequirements.find((br) => br.id === bookingRequirementRefId);
    const paxRequirementRefId = bookingRequirement?.paxRequirementRefId || [];
    const paxRequirement =
        state?.paxRequirements &&
        state.paxRequirements.filter((pax) => {
            const paxId = pax.id ?? -1;
            return paxRequirementRefId.includes(paxId);
        });
    return paxRequirement || [];
});

export const selectAvailabilitySelectedDate = createSelector(selectAvailabilityState, (state) => state.selectedDate);

export const selectIsOfferDateChipSelected = createSelector(
    selectAvailabilityState,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectOfferLevelCalendarsFromCalendarState,
    selectBasedCalendarOfferIds,
    (state, selectedOfferId, offerCalendars, allOfferIds) => {
        const offerOptions = state.offerOptionsState[selectedOfferId];
        if (!offerOptions) return false;
        const { selectedDate } = offerOptions;

        if (allOfferIds.length === 0 || !selectedDate) return false;
        const allSchedules = allOfferIds.reduce<AvailabilitySchedule[]>((acc, offerId) => {
            if (!offerId) return acc;
            const schedules = offerCalendars?.[offerId] || [];
            return acc.concat(schedules);
        }, []);
        const matchedSchedules = allSchedules.filter((schedule) => schedule.date === format(selectedDate, 'yyyy-MM-dd'));
        const isSelectedDateAvailable = matchedSchedules.length === 0 || matchedSchedules.some((schedule) => schedule.status === ActivityAvailabilityStatus.AVAILABLE);
        return isSelectedDateAvailable;
    },
);

export const selectIsShowDateChipNotification = createSelector(selectAvailabilityState, (state) => state.offerNotificationState[OfferNotificationType.DateChip]);
export const selectIsShowOptionSpecNotification = createSelector(selectAvailabilityState, (state) => state.offerNotificationState[OfferNotificationType.OptionSpec]);

export const selectIsShowDateNotAvailableNotification = createSelector(
    selectAvailabilityState,
    (state) => state.detailsPageNotificationType[ContinueYourBookingNotificationType.DateNotAvailable],
);

export const selectIsCartEligibleSelected = createSelector(
    selectBehaviorFromBootstrap,
    selectAvailabilityState,
    selectOfferGroupIdFromSelectedOfferGroup,
    selectConfigFromBootstrap,
    (behavior, availabilityState, selectedOfferId, config) => {
        if (selectIsAddAnyItemsToCart(behavior)) {
            return getShoppingCartBehavior(config);
        }

        const isCartEligible = availabilityState.offerOptionsState[selectedOfferId]?.isCartEligible;
        return isCartEligible ? getShoppingCartBehavior(config) : false;
    },
);

export const selectPaxInfo = createSelector(selectPassengerOption, (passengers) => {
    return passengers.map((passenger) => ({
        paxType: paxTypeMapper(passenger.code),
        quantity: passenger.quantity.value,
        ages: [],
    }));
});

export const selectAvailabilitySelectedOfferDetail = createSelector(
    selectActivityDetailOffer,
    selectOfferDetailRefSelected,
    (detailOffer, offerDetailRef): Optional<OfferDetail> => {
        const offerDetail = detailOffer.find((d) => d.offerDetailRef === offerDetailRef);
        return offerDetail;
    },
);

const getChargeTotal = (enrichedOfferOption?: EnrichedOfferOption[]): number => {
    const pricingSummary = getOfferWithMinPricingSummary(enrichedOfferOption || [])?.pricingSummary;
    if (!pricingSummary?.pricing) return 0;

    const displayPricing = selectDisplayPricing(pricingSummary.pricing[0]);
    return displayPricing?.allInclusive?.chargeTotal || 0;
};
const sortByChargeTotal = (a: Offer, b: Offer) => getChargeTotal(a.enrichedOfferOption) - getChargeTotal(b.enrichedOfferOption);
export const selectCheapestAvailableOffer = createSelector(selectAvailableOffers, (offers) => {
    return offers.sort(sortByChargeTotal)[0];
});

export const selectPageContentStatus = createSelector(selectAvailabilityState, (state: AvailabilityState): PageContentStatus => {
    if (state.isLoading || isDefaultGqlState(state)) {
        return PageContentStatus.Loading;
    }

    if (state.isError) {
        return PageContentStatus.StandardError;
    }

    if (!state.availabilityResponse || !state.isSuccess) {
        return PageContentStatus.SupplyError;
    }
    return PageContentStatus.Success;
});

export const selectInclusionExclusionFromDetail = createSelector(selectActivityDetail, (detail) => {
    const inclusions = (detail && detail.inclusions && detail.inclusions.length > 0 && detail.inclusions) || undefined;
    const exclusions = (detail && detail.exclusions && detail.exclusions.length > 0 && detail.exclusions) || undefined;

    return { inclusions, exclusions };
});

export const selectRedemptionFromDetail = createSelector(selectActivityDetail, (detail) => {
    return detail?.ticketing?.redemption || undefined;
});

export const selectTotalMaxPax = createSelector(selectSelectedBookingRequirement, selectFinalOfferOption, (selectedBookingRequirement, finalOfferOption): number => {
    const selectedOfferOptionAvailableQuantity = finalOfferOption?.availableQuantity || Infinity;
    // Note: We need to check both booking total pax limit and available quantity that's left for selected offerOption
    return Math.min(selectedBookingRequirement.maxPax, selectedOfferOptionAvailableQuantity);
});
